import { isSuccess } from '@devexperts/remote-data-ts'
import { Trans } from 'react-i18next'

import { useWhiteLabelBgTextClassName } from '@/hooks/useWhiteLabelBgTextClassName.ts'
import { QuoteSetupItems } from '@/models/cart'
import { Service } from '@/models/service'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { getGlobalBoughtQuantity } from '@/utils/getGlobalBoughtQuantity'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { useAppSelector } from '../../hooks/store'
import { cartSlice } from '../../store/cartSlice/index'
import { servicesSlice } from '../../store/servicesSlice'
import { Ticket } from './Ticket'

interface Props {
  maximumQuantity: number
  reservedQuantity: number
  availableQuantity: number
  hasLimit: boolean
  name: string
  tickets: Service[]
  serviceGroupId: number
}

export const Group = (props: Props) => {
  const services = useAppSelector(servicesSlice.selectors.services)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)

  const dayDifference = useAppSelector(dayDifferenceSelector)

  const headingClassName = useWhiteLabelBgTextClassName('text-lg font-bold')
  const paragraphClassName = useWhiteLabelBgTextClassName('mt-1')
  const ticketDefaults: QuoteSetupItems['boughtServices'] = {}

  if (!isSuccess(services) || props.tickets.length === 0) return

  const firstCartReservation = cartReservations[0] ?? undefined
  Object.keys(firstCartReservation?.boughtServices ?? {}).forEach((key) => {
    const serviceId = Number(key)
    ticketDefaults[serviceId] = firstCartReservation.boughtServices[Number(key)]
  })
  const products = {
    b: 0,
    boughtServices: {
      ...ticketDefaults,
    },
    c: 0,
    d: 0,
    m: 0,
  }

  return (
    <div className="mb-8">
      <h2 className={headingClassName}>{props.name}</h2>

      {props.hasLimit &&
      props.availableQuantity > 0 &&
      props.availableQuantity < SERVICE_LABEL_LIMIT ? (
            <p className={paragraphClassName}>
              <Trans
                i18nKey="tickets.group.max"
                tOptions={{ value: props.availableQuantity }}
              />
            </p>
          ) : null}

      <div className="mt-2 grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {props.tickets.map((ticket) => {
          const totalGroupBoughtQuantity = getGroupBoughtQuantity({
            groupId: ticket.serviceGroupId,
            selectedElementQuotableProducts: products,
            ticketServices: props.tickets,
          })
          const totalGlobalBoughtQuantity = getGlobalBoughtQuantity(products)
          // Final availability including the quantities bought on other services
          const availableGlobally = Math.max(
            0,
            Math.min(
              services.value.availableQuantity - totalGlobalBoughtQuantity,
              (services.value.maximumQuantity > 0
                ? services.value.maximumQuantity
                : Infinity) -
                services.value.reservedQuantity -
                totalGlobalBoughtQuantity
            )
          )
          const availableOnGroup = Math.max(
            0,
            Math.min(
              props.availableQuantity - totalGroupBoughtQuantity,
              (props.maximumQuantity > 0 ? props.maximumQuantity : Infinity) -
                props.reservedQuantity -
                totalGroupBoughtQuantity
            )
          )

          return (
            <Ticket
              availability={{
                global: availableGlobally,
                group: availableOnGroup,
                ticket: getServiceSingleLimit({
                  dayDifference,
                  service: ticket,
                }),
              }}
              key={ticket.serviceId}
              ticket={ticket}
            />
          )
        })}
      </div>
    </div>
  )
}
